/**
 * Benefits data for the landing page Benefits section.
 *
 * Each entry provides an icon factory that returns a fresh inline SVG
 * element and an i18n key prefix under the `landing:benefits` namespace.
 *
 * @module landing/content/benefits
 */

import React from 'react';

/** Describes a single benefit item. */
export interface BenefitItem {
  /** Unique identifier for the benefit. */
  id: string;
  /** i18n key prefix under `landing:benefits` (e.g. "compare"). */
  i18nKey: string;
  /** Factory returning a fresh inline SVG icon element using currentColor. */
  icon: () => React.ReactElement;
}

/** Shared SVG attributes for all benefit icons. */
const SVG_ATTRS = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  width: 24,
  height: 24,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
} as const;

/** Benefits displayed on the landing page. */
export const BENEFITS: BenefitItem[] = [
  {
    id: 'compare',
    i18nKey: 'compare',
    icon: () =>
      React.createElement(
        'svg',
        SVG_ATTRS,
        React.createElement('path', {
          d: 'M12 3v18M3 12h18M8 8l-4 4 4 4M16 8l4 4-4 4',
        }),
      ),
  },
  {
    id: 'enroll',
    i18nKey: 'enroll',
    icon: () =>
      React.createElement(
        'svg',
        SVG_ATTRS,
        React.createElement('circle', { cx: 12, cy: 12, r: 10 }),
        React.createElement('polyline', { points: '12 6 12 12 16 14' }),
      ),
  },
  {
    id: 'trusted',
    i18nKey: 'trusted',
    icon: () =>
      React.createElement(
        'svg',
        SVG_ATTRS,
        React.createElement('path', {
          d: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
        }),
        React.createElement('polyline', { points: '9 12 11 14 15 10' }),
      ),
  },
  {
    id: 'support',
    i18nKey: 'support',
    icon: () =>
      React.createElement(
        'svg',
        SVG_ATTRS,
        React.createElement('path', {
          d: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2',
        }),
        React.createElement('circle', { cx: 12, cy: 7, r: 4 }),
      ),
  },
];
